import { Fragment, memo, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useAuth } from '../../../../context/AuthContext';
import { useWorkOrderData, useWorkOrderLayout, useWorkOrderSelection } from '../../../../context/WorkOrdersContext';
import { WorkOrderStatus } from '../../../../enums/workOrderStatus';
import type { WorkOrderDragItem, WorkOrderFilterRule } from '../../../../types/InfrastructureManagerTypes';
import { useWorkOrderSettings } from '../../../../hooks/useWorkOrderSettings';
import { WORK_ORDER_DRAG_ROLES } from '../constants';
import { applyFilters, filterBySearch, groupByStatus } from '../workOrderList.utils';
import ColumnResizeHandle from './ColumnResizeHandle';
import StatusColumn from './StatusColumn';
import { useColumnWidths } from './useColumnWidths';
import styles from './WorkOrderStatusList.module.scss';

interface WorkOrderStatusListProps {
  searchTerm: string;
  filters: WorkOrderFilterRule[];
}

const WorkOrderStatusList = ({ searchTerm, filters }: WorkOrderStatusListProps) => {
  const { orders, updateStatus } = useWorkOrderData();
  const { selectedId, toggleSelect } = useWorkOrderSelection();
  const { columnSorts, setColumnSort, columnWidthsRef, openDetail } = useWorkOrderLayout();
  const { canAccess } = useAuth();
  const canDrag = canAccess({ allowedRoles: WORK_ORDER_DRAG_ROLES });
  const { settings, visibleStatuses } = useWorkOrderSettings();

  const grouped = useMemo(() => groupByStatus(applyFilters(filterBySearch(orders, searchTerm), filters)), [orders, searchTerm, filters]);
  const { listRef, startResize, nudge } = useColumnWidths(visibleStatuses, columnWidthsRef);

  // A drop applies at once, dated today: Completed on today, and Scheduled for today unless it already has dates.
  const handleDrop = useCallback(
    (item: WorkOrderDragItem, to: WorkOrderStatus) => {
      const today = dayjs().startOf('day').toISOString();
      if (to === WorkOrderStatus.COMPLETED) updateStatus(item.id, to, { completedAt: today });
      else if (to === WorkOrderStatus.SCHEDULED && !item.hasScheduleDates) updateStatus(item.id, to, { scheduleDates: [today] });
      else updateStatus(item.id, to);
    },
    [updateStatus],
  );

  return (
    <div ref={listRef} className={styles.statusList} role="group" aria-label="Work orders by status">
      {visibleStatuses.map((status) => (
        <Fragment key={status}>
          <StatusColumn
            status={status}
            orders={grouped[status]}
            sort={columnSorts[status]}
            color={settings.statuses[status].color}
            selectedId={selectedId}
            canDrag={canDrag}
            onSortChange={setColumnSort}
            onSelect={toggleSelect}
            onExpand={openDetail}
            onDrop={handleDrop}
          />
          <ColumnResizeHandle status={status} onResizeStart={startResize} onNudge={nudge} />
        </Fragment>
      ))}
    </div>
  );
};

export default memo(WorkOrderStatusList);
